/**
 * Stage display: huge MM:SS on black, local emergency controls in a panel.
 * All controls work fully offline; the server is only informed afterwards.
 */
import { boot } from '../lib/boot.js';
import { $, setAttr, setText } from '../lib/dom.js';
import { startRenderLoop } from '../lib/render-loop.js';
import { startWakeLock, WAKE_LOCK_LABELS } from '../lib/wake-lock.js';
import { fullscreenSupported, isFullscreen, toggleFullscreen } from '../lib/fullscreen.js';
import { createConfirm } from '../lib/confirm.js';
import { guardTap } from '../lib/tap-guard.js';
import { PRESETS, type PresetId } from '../../shared/state.js';
import { STATUS_LABELS } from '../../shared/format.js';

const { client, socket, storage, room } = boot('display');

const timerEl = $('#timer');
const panel = $('#panel');
const openBtn = $<HTMLButtonElement>('#open-panel');
const closeBtn = $<HTMLButtonElement>('#close-panel');
const panelTime = $('#panel-time');
const panelPreset = $('#panel-preset');
const panelStatus = $('#panel-status');
const connDot = $('#conn-dot');
const connStatus = $('#conn-status');
const wakeStatus = $('#wakelock-status');
const roomStatus = $('#room-status');
const pinSection = $('#pin-section');
const pinForm = $<HTMLFormElement>('#pin-form');
const pinInput = $<HTMLInputElement>('#pin-input');
const pinHint = $('#pin-hint');
const noRoomHint = $('#no-room-hint');
const startBtn = $<HTMLButtonElement>('#btn-start');
const pauseBtn = $<HTMLButtonElement>('#btn-pause');
const resetBtn = $<HTMLButtonElement>('#btn-reset');
const fsBtn = $<HTMLButtonElement>('#btn-fullscreen');
const presetBtns: Record<PresetId, HTMLButtonElement> = {
  talk: $<HTMLButtonElement>('#btn-talk'),
  qa: $<HTMLButtonElement>('#btn-qa'),
};

const confirm = createConfirm($('#confirm'));

// ---------------------------------------------------------------- rendering
function render(now: number): void {
  const snap = client.snapshot(now);
  setText(timerEl, snap.text);
  setAttr(timerEl, 'data-color', snap.color);
  setAttr(timerEl, 'data-status', snap.status);
  if (!panel.hidden) {
    setText(panelTime, snap.text);
    setText(panelPreset, snap.presetLabel);
    setText(panelStatus, STATUS_LABELS[snap.status]);
    startBtn.disabled = snap.status === 'running' || snap.status === 'finished';
    pauseBtn.disabled = snap.status !== 'running';
    for (const id of Object.keys(presetBtns) as PresetId[]) {
      presetBtns[id].classList.toggle('btn-selected', id === snap.presetId);
      presetBtns[id].setAttribute('aria-pressed', String(id === snap.presetId));
    }
  }
}

function renderConnection(): void {
  const open = client.connection === 'open';
  connDot.className = `dot ${open ? 'dot-ok' : 'dot-warn'}`;
  if (client.connection === 'bad_room') {
    setText(connStatus, 'Ungültiger Room-Link');
  } else if (open) {
    setText(connStatus, client.canControl ? 'Remote verbunden' : 'Remote verbunden (nur Empfang)');
  } else {
    setText(connStatus, 'Offline / lokale Steuerung');
  }
  const needsPin = open && !client.canControl;
  pinSection.hidden = !needsPin;
  if (client.lastError === 'bad_pin') {
    setText(pinHint, 'PIN falsch. Bitte erneut eingeben.');
  }
}

startRenderLoop(render);
client.subscribe((ev) => {
  if (ev.type === 'state') render(Date.now());
  else renderConnection();
});
renderConnection();
setText(roomStatus, room ? `${room.slice(0, 4)}…` : '–');
noRoomHint.hidden = room !== null;

// ---------------------------------------------------------------- panel
function openPanel(): void {
  panel.hidden = false;
  openBtn.setAttribute('aria-expanded', 'true');
  render(Date.now());
  closeBtn.focus();
}
function closePanel(): void {
  panel.hidden = true;
  openBtn.setAttribute('aria-expanded', 'false');
}
guardTap(openBtn, openPanel, 300);
guardTap(closeBtn, closePanel, 300);

// ---------------------------------------------------------------- commands
guardTap(startBtn, () => {
  client.dispatch({ type: 'START' });
});
guardTap(pauseBtn, () => {
  client.dispatch({ type: 'PAUSE' });
});
guardTap(resetBtn, async () => {
  if (client.snapshot().status === 'running') {
    const ok = await confirm({
      message: 'Der Timer läuft gerade. Wirklich zurücksetzen?',
      confirmLabel: 'Zurücksetzen',
    });
    if (!ok) return;
  }
  client.dispatch({ type: 'RESET' });
});
for (const id of Object.keys(presetBtns) as PresetId[]) {
  guardTap(presetBtns[id], async () => {
    const preset = PRESETS[id];
    if (client.snapshot().status === 'running') {
      const ok = await confirm({
        message: `Der Timer läuft gerade. Wirklich auf ${preset.label} ${mmss(preset.durationMs)} wechseln?`,
        confirmLabel: 'Wechseln',
      });
      if (!ok) return;
    }
    client.dispatch({ type: 'SET_PRESET', presetId: id });
  });
}

function mmss(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------- PIN
pinForm.addEventListener('submit', (ev) => {
  ev.preventDefault();
  const pin = pinInput.value.trim();
  if (!pin) return;
  storage.savePin(pin);
  pinInput.value = '';
  client.lastError = null;
  setText(pinHint, 'PIN gespeichert, verbinde neu…');
  socket?.restart();
});

// ---------------------------------------------------------------- wake lock / fullscreen
startWakeLock((status) => setText(wakeStatus, WAKE_LOCK_LABELS[status]));

if (fullscreenSupported()) {
  fsBtn.hidden = false;
  guardTap(fsBtn, async () => {
    await toggleFullscreen();
    setText(fsBtn, isFullscreen() ? 'Vollbild beenden' : 'Vollbild');
  });
  document.addEventListener('fullscreenchange', () => {
    setText(fsBtn, isFullscreen() ? 'Vollbild beenden' : 'Vollbild');
  });
}

console.info(`[display] build ${__BUILD_ID__}, client ${client.snapshot().version}`);
